// src/components/ClaimVenueSheet.jsx
import React, { useEffect, useMemo, useState } from 'react';

const DEFAULT_BACKEND =
  (typeof window !== 'undefined' && window.__PARTY_BACKEND__) ||
  'https://partybutton-private-a027fa6fce78.herokuapp.com';

const MAX_UPLOAD_BYTES = 6 * 1024 * 1024;

const emptyForm = () => ({
  contactName: '',
  businessEmail: '',
  websiteOrSocial: '',
  role: '',
  proofOfControl: '',
  consentToContact: false,
  rightsConfirmed: false,
});

function getVenueId(venue) {
  return venue?.place_id || venue?.placeId || venue?.id || '';
}

function looksLikeEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

export default function ClaimVenueSheet({
  open,
  venue,
  backendBase,
  partyUid,
  onClose,
  onSubmitted,
}) {
  const [form, setForm] = useState(emptyForm);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const placeId = getVenueId(venue);
  const venueName = String(venue?.name || '').trim();

  const base = useMemo(
    () => String(backendBase || DEFAULT_BACKEND).replace(/\/+$/, ''),
    [backendBase]
  );

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm());
    setPhoto(null);
    setBusy(false);
    setError('');
    setSuccess('');
  }, [open, placeId]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onClose?.();
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, busy, onClose]);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError('');
    setSuccess('');
  };

  const canSubmit = Boolean(
    placeId &&
      form.contactName.trim() &&
      looksLikeEmail(form.businessEmail) &&
      form.websiteOrSocial.trim() &&
      form.role.trim() &&
      form.proofOfControl.trim() &&
      form.consentToContact &&
      form.rightsConfirmed &&
      photo &&
      !busy &&
      !success
  );

  const submit = async () => {
    setError('');
    setSuccess('');

    if (!placeId) {
      setError('Select a business first.');
      return;
    }
    if (!form.contactName.trim()) {
      setError('Enter your contact name.');
      return;
    }
    if (!looksLikeEmail(form.businessEmail)) {
      setError('Enter a valid business email.');
      return;
    }
    if (!form.websiteOrSocial.trim()) {
      setError('Add the business website or social profile.');
      return;
    }
    if (!form.role.trim()) {
      setError('Tell us your role at the business.');
      return;
    }
    if (!form.proofOfControl.trim()) {
      setError('Tell us how we can verify that you represent the business.');
      return;
    }
    if (!photo) {
      setError('Choose a current photo of the business.');
      return;
    }
    if (photo.size > MAX_UPLOAD_BYTES) {
      setError('Image is too large. Please upload 6MB or less.');
      return;
    }
    if (!form.rightsConfirmed) {
      setError('Confirm that you own the photo or have permission to use it.');
      return;
    }
    if (!form.consentToContact) {
      setError('Consent is required so we can verify the submission.');
      return;
    }

    setBusy(true);

    try {
      const fd = new FormData();
      fd.append('venueName', venueName);
      fd.append('placeId', String(placeId));
      fd.append('contactName', form.contactName.trim().slice(0, 160));
      fd.append('businessEmail', form.businessEmail.trim().slice(0, 240));
      fd.append('websiteOrSocial', form.websiteOrSocial.trim().slice(0, 500));
      fd.append('role', form.role.trim().slice(0, 160));
      fd.append('proofOfControl', form.proofOfControl.trim().slice(0, 1500));
      fd.append('consentToContact', 'true');
      fd.append('rightsConfirmed', 'true');
      fd.append('image', photo, photo.name || 'business-photo.jpg');

      const response = await fetch(`${base}/api/claims`, {
        method: 'POST',
        headers: {
          ...(partyUid ? { 'X-Party-Uid': String(partyUid) } : {}),
        },
        body: fd,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }

      setSuccess(
        'Submitted for review. The current image stays in place until your photo is approved.'
      );
      onSubmitted?.(data);
    } catch (submitError) {
      setError(submitError?.message || 'Failed to submit the business photo.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="owner-photo-backdrop"
      role="presentation"
      onClick={(event) => {
        if (!busy && event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="owner-photo-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Business owner photo submission"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="owner-photo-handle" />

        <div className="owner-photo-header">
          <div>
            <div className="owner-photo-title">Business Owner? Add a Photo</div>
            <div className="owner-photo-subtitle">
              Free photo submission for verified owners and managers.
            </div>
          </div>
          <button
            type="button"
            className="owner-photo-close"
            onClick={() => onClose?.()}
            disabled={busy}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="owner-photo-body">
          <div className="owner-photo-explainer">
            <strong>{venueName || 'Selected business'}</strong>
            <span>
              Once approved, your photo may replace the generic image shown for this venue.
              It will not change rankings, ratings, or Top Pick status.
            </span>
          </div>

          <label className="owner-photo-field">
            <span>Contact name</span>
            <input
              value={form.contactName}
              onChange={(event) => update('contactName', event.target.value)}
              autoComplete="name"
              placeholder="Your name"
            />
          </label>

          <label className="owner-photo-field">
            <span>Business email</span>
            <input
              type="email"
              value={form.businessEmail}
              onChange={(event) => update('businessEmail', event.target.value)}
              autoComplete="email"
              placeholder="you@business.com"
            />
          </label>

          <label className="owner-photo-field">
            <span>Business website or social profile</span>
            <input
              value={form.websiteOrSocial}
              onChange={(event) => update('websiteOrSocial', event.target.value)}
              autoCapitalize="none"
              placeholder="Website, Instagram, Facebook, etc."
            />
          </label>

          <label className="owner-photo-field">
            <span>Your role</span>
            <input
              value={form.role}
              onChange={(event) => update('role', event.target.value)}
              placeholder="Owner, manager, marketing lead…"
            />
          </label>

          <label className="owner-photo-field">
            <span>Proof that you represent this business</span>
            <textarea
              value={form.proofOfControl}
              onChange={(event) => update('proofOfControl', event.target.value)}
              rows={3}
              placeholder="Example: I can reply from the business email, update the website, or confirm through the listed phone number."
            />
          </label>

          <label className="owner-photo-field">
            <span>Current business photo</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/heic,image/heif"
              onChange={(event) => {
                const nextPhoto = event.target.files?.[0] || null;
                if (nextPhoto && nextPhoto.size > MAX_UPLOAD_BYTES) {
                  setPhoto(null);
                  setError('Image is too large. Please upload 6MB or less.');
                  return;
                }
                setPhoto(nextPhoto);
                setError('');
                setSuccess('');
              }}
            />
            <small>JPG, PNG, HEIC or HEIF · up to 6MB</small>
          </label>

          <label className="owner-photo-check">
            <input
              type="checkbox"
              checked={form.rightsConfirmed}
              onChange={(event) => update('rightsConfirmed', event.target.checked)}
            />
            <span>I own this photo or have permission to submit it.</span>
          </label>

          <label className="owner-photo-check">
            <input
              type="checkbox"
              checked={form.consentToContact}
              onChange={(event) => update('consentToContact', event.target.checked)}
            />
            <span>The Party Button may contact me to verify this submission.</span>
          </label>

          {error ? <div className="owner-photo-error">{error}</div> : null}
          {success ? <div className="owner-photo-success">{success}</div> : null}

          <button
            type="button"
            className="owner-photo-submit"
            disabled={!canSubmit}
            onClick={submit}
          >
            {busy ? 'Submitting…' : success ? 'Submitted' : 'Submit photo for review'}
          </button>

          <div className="owner-photo-privacy">
            Your contact details are used only to review and verify the business submission.
            Approved owner photos are not sponsored placements.
          </div>
        </div>
      </div>

      <style>{`
        .owner-photo-backdrop{
          position:fixed; inset:0; z-index:10020; padding:10px;
          padding-bottom:calc(10px + env(safe-area-inset-bottom));
          background:rgba(0,0,0,.48); display:flex; align-items:flex-end;
          justify-content:center;
        }
        .owner-photo-sheet{
          width:min(720px,100%); max-height:90vh; overflow:auto;
          background:rgba(255,255,255,.98); border-radius:22px 22px 18px 18px;
          border:1px solid rgba(0,0,0,.08); box-shadow:0 -12px 30px rgba(0,0,0,.24);
          color:#171717;
        }
        .owner-photo-handle{
          width:58px; height:6px; border-radius:999px; margin:10px auto 2px;
          background:rgba(0,0,0,.16);
        }
        .owner-photo-header{
          display:flex; align-items:flex-start; justify-content:space-between;
          gap:12px; padding:10px 14px 8px;
        }
        .owner-photo-title{font-size:19px; font-weight:900;}
        .owner-photo-subtitle{font-size:12px; font-weight:800; opacity:.68; margin-top:3px;}
        .owner-photo-close{
          border:0; border-radius:12px; padding:8px 10px; font-weight:900;
          background:rgba(0,0,0,.06); cursor:pointer;
        }
        .owner-photo-body{padding:0 14px 16px;}
        .owner-photo-explainer{
          display:flex; flex-direction:column; gap:5px; padding:11px 12px;
          border-radius:14px; background:rgba(255,45,179,.07); font-size:13px;
          line-height:18px;
        }
        .owner-photo-field{display:block; margin-top:12px; font-weight:900; font-size:13px;}
        .owner-photo-field > span{display:block; margin-bottom:6px; opacity:.82;}
        .owner-photo-field input,.owner-photo-field textarea{
          box-sizing:border-box; width:100%; border:1px solid rgba(0,0,0,.14);
          border-radius:13px; padding:10px 11px; background:#fff; color:#171717;
          font:inherit; font-weight:700; outline:none;
        }
        .owner-photo-field textarea{resize:vertical; line-height:18px;}
        .owner-photo-field small{display:block; margin-top:5px; opacity:.65;}
        .owner-photo-check{
          display:flex; align-items:flex-start; gap:9px; margin-top:12px;
          font-size:13px; font-weight:800; line-height:18px;
        }
        .owner-photo-check input{margin-top:3px; flex:0 0 auto;}
        .owner-photo-error,.owner-photo-success{
          margin-top:12px; padding:10px 11px; border-radius:13px;
          font-size:13px; font-weight:900; line-height:18px;
        }
        .owner-photo-error{background:rgba(210,0,0,.07); border:1px solid rgba(210,0,0,.18);}
        .owner-photo-success{background:rgba(0,160,80,.08); border:1px solid rgba(0,160,80,.2);}
        .owner-photo-submit{
          width:100%; border:0; border-radius:15px; padding:13px 12px;
          margin-top:14px; background:#ff2db3; color:#fff; font-size:15px;
          font-weight:900; cursor:pointer; box-shadow:0 6px 16px rgba(255,45,179,.22);
        }
        .owner-photo-submit:disabled{opacity:.48; cursor:not-allowed; box-shadow:none;}
        .owner-photo-privacy{margin-top:9px; font-size:11px; line-height:16px; font-weight:800; opacity:.62;}
      `}</style>
    </div>
  );
}
